import * as Yup from "yup";

/**
 * Filters a Formik values object to include only fields that have no associated errors.
 *
 * @template Values - An object type representing the structure of the Formik form values.
 * @param values - The Formik values object of type `Values`.
 * @param errors - The Formik errors object of type `FormikErrors<Values>`.
 * @returns An object containing only the valid field values of type `Partial<Values>`.
 */
export const getValidFormValues = <Values extends Record<string, any>>(
  values: Values,
  errors: Values,
): Partial<Values> => {
  return Object.fromEntries(
    Object.entries(values).filter(([key]) => !errors[key as keyof Values]),
  ) as Partial<Values>;
};

/**
 * Extracts valid values from a nested object by excluding fields with validation errors.
 *
 * @template Values - An object type representing the structure of the values object.
 * @param values - The original values object of type `Values`.
 * @param validationError - The `Yup.ValidationError` object containing validation errors.
 * @returns A new object containing only the valid fields, preserving the original structure.
 *
 * ### Behavior:
 * - Traverses the `values` object recursively to handle nested structures.
 * - Excludes fields that have corresponding error paths in the `validationError`.
 * - Preserves the structure of the original object for valid fields.
 *
 * ### Example:
 * ```typescript
 * const values = {
 *   name: "John",
 *   address: {
 *     street: "123 Main St",
 *     city: "New York",
 *   },
 *   age: 30,
 * };
 * const validationError = new Yup.ValidationError([
 *   { path: "address.city", message: "City is required" },
 * ]);
 *
 * const result = getValidValuesFromYupError(values, validationError);
 * // Result: { name: "John", address: { street: "123 Main St" }, age: 30 }
 */
export const getValidValuesFromYupError = <Values extends Record<string, any>>(
  values: Values,
  validationError: Yup.ValidationError,
): Partial<Values> => {
  const errorPaths = validationError.inner.reduce(
    (acc, err) => {
      if (err.path) {
        acc[err.path] = true;
      }
      return acc;
    },
    {} as Record<string, boolean>,
  );
  const getValidNestedValues = (
    obj: Record<string, any>,
    prefix: string,
  ): Partial<Values> => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      const currentPath = prefix ? `${prefix}.${key}` : key;
      if (
        typeof value === "object" &&
        value !== null &&
        !(value instanceof Date)
      ) {
        const nestedValidValues = getValidNestedValues(
          value as Record<string, any>,
          currentPath,
        );
        if (Object.keys(nestedValidValues).length > 0) {
          acc[key as keyof Values] = nestedValidValues as Values[keyof Values];
        }
      } else if (!errorPaths[currentPath]) {
        acc[key as keyof Values] = value as Values[keyof Values];
      }
      return acc;
    }, {} as Partial<Values>);
  };

  return getValidNestedValues(values, "");
};

/**
 * Compares two objects and determines if there are any differences in their shared keys.
 *
 * @template T - A generic type extending `Record<string, any>`, representing
 * the structure of the objects being compared.
 * @param previous - The first object to compare.
 * @param current - The second object to compare.
 * @returns `true` if any shared keys between the two objects have different values, `false` otherwise.
 *
 * ### Behavior:
 * - Performs a deep comparison for nested objects.
 * - Only compares keys that exist in both objects.
 * - Returns `true` if any value differs, including nested values.
 * - Ignores keys that are not shared between the two objects.
 *
 */
export function hasAnyDifferentValue<T extends Record<string, any>>(
  previous: T,
  current: T,
): boolean {
  const compareSharedKeys = (prevValue: any, currentValue: any): boolean => {
    if (prevValue === currentValue) {
      return false; // Values are strictly equal
    }

    const isObject = (val: any): val is Record<string, any> =>
      typeof val === "object" && val !== null;

    if (isObject(prevValue) && isObject(currentValue)) {
      // Both are objects, compare shared keys recursively
      const sharedKeys = Object.keys(prevValue).filter((key) =>
        currentValue.hasOwnProperty(key),
      );

      for (const key of sharedKeys) {
        if (compareSharedKeys(prevValue[key], currentValue[key])) {
          return true; // Nested values for a shared key are different
        }
      }
      return false; // All shared nested values are equal
    }

    return true; // Values are different (not both objects or strictly not equal)
  };

  const sharedTopLevelKeys = Object.keys(previous).filter((key) =>
    current.hasOwnProperty(key),
  );

  for (const key of sharedTopLevelKeys) {
    if (compareSharedKeys(previous[key], current[key])) {
      return true;
    }
  }

  return false;
}

export const trimValues = <T extends Record<string, any>>(obj: T): T => {
  const trimmed: Record<string, any> = {};

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value: string | any = obj[key];

      if (value instanceof Date) {
        trimmed[key] = value; // Keep Date objects as they are
      } else if (typeof value === "string") {
        trimmed[key] = value.trim();
      } else if (typeof value === "object" && value !== null) {
        // Recursively trim nested objects
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        trimmed[key] = trimValues(value);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        trimmed[key] = value;
      }
    }
  }

  return trimmed as T;
};
